import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import scrollToId from "@store/utilities/scrollToId";
import deburr from "lodash/deburr";
import React, { FC, useEffect, useMemo } from "react";
import { css } from "styled-components";

const enum fields {
    anchor = "anchor",
    anchorLabel = "anchorLabel",
    direction = "direction",
    offset = "offset",
}

interface Props extends WidgetProps {
    fields: {
        [fields.anchor]: string;
        [fields.anchorLabel]: string;
        [fields.direction]: string;
        [fields.offset]: string;
    };
}

function stripChar(string: string, char: string) {
    return string.substring(string.indexOf(char) + 1);
}

export interface AnchorHeadingStyles {
    anchorHeading?: AnchorHeadingStyles;
}

const textToSafeId = (text: string, separator = "-") => {
    const cleanupRegex = new RegExp(`^${separator}|${separator}$`, "g");

    return deburr(text.toLowerCase()).replace(/\W+/g, separator).replace(cleanupRegex, "");
};

const ProductDetailsAnchorHeading: FC<Props> = ({ fields: { anchor, direction, offset } }) => {
    const { isInShell } = useShellContext();
    const safeAnchor = useMemo(() => textToSafeId(anchor), [anchor]);
    const windowExist = typeof window !== "undefined";
    const windowLocation = () => {
        if (windowExist) {
            return window.location;
        }
    };
    const initLocation = windowLocation(); // hack because location hash not picked up on pageload

    useEffect(() => {
        let anchor = "";
        if (initLocation && initLocation.hash) {
            anchor = stripChar(initLocation.hash, "#");
        }
        if (anchor) {
            scrollToId(anchor);
        }
    }, []);

    const invisibleStyles = css`
        width: 100%;
        height: 0;
        position: relative;
        top: ${direction === "top" && "-"}${offset}px;
        ${isInShell &&
        `
        text-align: center;
        color: red;
        background: rgba(255,255,255,0.5);
        height: auto;
        border: 1px dashed red;
        padding: 3px;
        `}
    `;

    return (
        <StyledWrapper css={invisibleStyles} id={safeAnchor}>
            {isInShell && anchor}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: ProductDetailsAnchorHeading,
    definition: {
        group: "Basic",
        displayName: "Anchor",
        icon: "anchor",
        fieldDefinitions: [
            {
                name: fields.anchor,
                displayName: "Anchor",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                isRequired: true,
                defaultValue: "",
            },
            {
                name: fields.direction,
                displayName: "Offset Direction",
                editorTemplate: "RadioButtonsField",
                defaultValue: "top",
                fieldType: "General",
                sortOrder: 2,
                options: [
                    {
                        displayName: "Offset to Top",
                        value: "top",
                    },
                    {
                        displayName: "Offset to Bottom",
                        value: "bottom",
                    },
                ],
            },
            {
                name: fields.offset,
                displayName: "Offset Distance in Pixels",
                editorTemplate: "TextField",
                defaultValue: "0",
                fieldType: "General",
                sortOrder: 3,
            },
        ],
    },
};

export default widgetModule;
